import { redactDicomUids } from './phiRedaction';

/** Console methods whose output is kept for diagnostics. */
const BUFFERED_LEVELS = ['error', 'warn', 'info', 'debug', 'log'] as const;

export type ConsoleLevel = (typeof BUFFERED_LEVELS)[number];

export interface ConsoleEntry {
    /** ISO 8601, UTC. */
    time: string;
    level: ConsoleLevel;
    /** Arguments joined into one line, DICOM UIDs redacted, length-capped. */
    message: string;
}

const MAX_ENTRIES = 100;
const MAX_MESSAGE_LENGTH = 1000;
const MAX_SERIALISED_ARGUMENT_LENGTH = 300;

const entries: ConsoleEntry[] = [];
let installed = false;

function describeArgument(argument: unknown): string {
    if (typeof argument === 'string') {
        return argument;
    }
    if (argument instanceof Error) {
        return `${argument.name}: ${argument.message}`;
    }
    if (
        argument === null ||
        argument === undefined ||
        typeof argument !== 'object'
    ) {
        return String(argument);
    }
    try {
        const serialised = JSON.stringify(argument);
        return serialised.length > MAX_SERIALISED_ARGUMENT_LENGTH
            ? `${serialised.slice(0, MAX_SERIALISED_ARGUMENT_LENGTH)}…`
            : serialised;
    } catch {
        // Circular or otherwise unserialisable values.
        return Object.prototype.toString.call(argument);
    }
}

/** One console call as a single redacted line of bounded length. */
export function formatConsoleArguments(args: readonly unknown[]): string {
    const message = redactDicomUids(args.map(describeArgument).join(' '));
    return message.length > MAX_MESSAGE_LENGTH
        ? `${message.slice(0, MAX_MESSAGE_LENGTH)}… (truncated)`
        : message;
}

function record(level: ConsoleLevel, args: readonly unknown[]): void {
    entries.push({
        time: new Date().toISOString(),
        level,
        message: formatConsoleArguments(args),
    });
    if (entries.length > MAX_ENTRIES) {
        entries.splice(0, entries.length - MAX_ENTRIES);
    }
}

/**
 * Keeps the most recent console output in memory so the error page can show
 * what led up to a crash, also where no Sentry DSN is configured. Wraps the
 * console methods once; output still reaches the original console. A failure
 * to record never affects the logging call itself.
 */
export function installConsoleBuffer(): void {
    if (installed) {
        return;
    }
    installed = true;
    for (const level of BUFFERED_LEVELS) {
        // eslint-disable-next-line no-console
        const original = console[level];
        // eslint-disable-next-line no-console
        console[level] = (...args: unknown[]) => {
            try {
                record(level, args);
            } catch {
                // Diagnostics must never break logging.
            }
            original.apply(console, args);
        };
    }
}

/** A copy of the buffered entries, oldest first. */
export function getConsoleEntries(): ConsoleEntry[] {
    return entries.map((entry) => ({ ...entry }));
}

/** Clears the buffer; for tests. */
export function clearConsoleEntries(): void {
    entries.length = 0;
}
